// src/components/admin/nornsweave/PrintTab.js
import React, { useRef } from 'react';
import { useAdminNornsWeave } from './AdminNornsWeaveContext';
import { formatTime12 } from '../../TimePicker';

function PrintTab({ timeSlots, getEventsForSlot, getWeekLabel }) {
  const {
    weekOffset,
    setWeekOffset,
    weekDates,
    settingsForm,
    aides
  } = useAdminNornsWeave();

  const printRef = useRef();
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      <div className="bg-white rounded-xl shadow-lg p-6 mb-4 print:hidden">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Print Schedule</h2>
            <p className="text-slate-600 text-sm mt-1">Generate a printable weekly schedule</p>
          </div>
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWeekOffset(prev => prev - 1)}
                className="bg-white border-2 border-purple-200 text-purple-700 px-3 py-1 rounded hover:bg-purple-50"
              >
                ←
              </button>
              <span className="font-medium">{getWeekLabel()}</span>
              <button
                onClick={() => setWeekOffset(prev => prev + 1)}
                className="bg-white border-2 border-purple-200 text-purple-700 px-3 py-1 rounded hover:bg-purple-50"
              >
                →
              </button>
            </div>
            <button
              onClick={handlePrint}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Printable Schedule */}
      <div ref={printRef} className="bg-white rounded-xl shadow-lg p-6 print:shadow-none print:p-2">
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold">{settingsForm.schoolName || 'Library'} Schedule</h1>
          <p className="text-slate-600">Week of {weekDates[0]} - {weekDates[4]}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {['elementary', 'middle'].map(library => (
            <div key={library} className="border rounded-lg overflow-hidden">
              <div className={`text-white text-center py-2 font-bold ${library === 'elementary' ? 'bg-purple-600' : 'bg-indigo-600'}`}>
                {library === 'elementary' ? 'Elementary' : 'Middle School'}
              </div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-1 py-1">Time</th>
                    {dayNames.map(day => (
                      <th key={day} className="border px-1 py-1">{day.slice(0, 3)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {timeSlots.slice(0, -1).map((timeSlot, slotIdx) => {
                    const nextSlot = timeSlots[slotIdx + 1];
                    return (
                      <tr key={timeSlot}>
                        <td className="border px-1 py-0.5 font-medium bg-gray-50">{timeSlot}</td>
                        {weekDates.map(date => {
                          const events = getEventsForSlot(library, date, timeSlot, nextSlot);
                          return (
                            <td key={date} className="border px-1 py-0.5">
                              {events.map((e, i) => (
                                <div key={i} className="truncate">
                                  {e.grade || e.title || e.reason || e.type}
                                </div>
                              ))}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {aides.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <h3 className="font-bold mb-2">Aide Schedule</h3>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {aides.map(aide => (
                <div key={aide.id}>
                  <span className="font-medium">{aide.name}:</span>
                  {aide.schedule?.map((s, i) => (
                    <span key={i} className="ml-1">{s.day.slice(0, 3)} {formatTime12(s.startTime)}-{formatTime12(s.endTime)}</span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PrintTab;
